import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Toolbar,
  AppBar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Save as SaveIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../services/api';
import { FeedbackWidget, useTextSelectionFeedback } from '../../components/feedback';

interface Document {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

const DocumentEditorPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const quillRef = useRef<ReactQuill>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [document, setDocument] = useState<Document | null>(null);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Feedback states
  const [showTextFeedback, setShowTextFeedback] = useState(false);
  
  // Determinar feature de feedback baseado na origem
  const feedbackFeature = location.state?.fromImprovement 
    ? 'document_improvement' 
    : location.state?.fromCreation 
      ? 'document_creation' 
      : 'document_editor';
  
  // Hook para feedback de texto selecionado
  const {
    selectedText,
    showFeedbackButton,
    buttonPosition,
    clearSelection,
    getTextContext,
    isTextSelected,
  } = useTextSelectionFeedback({
    containerRef: editorContainerRef,
    minLength: 10,
    enabled: true,
  });

  useEffect(() => {
    if (documentId) {
      loadDocument();
    }
  }, [documentId]);

  // Configuração do editor Quill
  const modules = {
    toolbar: [
      [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image'],
      [{ 'align': [] }],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image',
    'align', 'color', 'background'
  ];

  const loadDocument = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/ai-documents/document/${documentId}/preview`);
      
      if (response.data.status === 'success') {
        const docData = response.data.data;
        setDocument(docData);
        setTitle(docData.title);
        
        // Converter quebras de linha para HTML
        const htmlContent = docData.content.replace(/\n/g, '<br>');
        setContent(htmlContent);
      }
    } catch (err: any) {
      console.error('Erro ao carregar documento:', err);
      setError('Erro ao carregar documento');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (value: string) => {
    setContent(value);
    setHasUnsavedChanges(true);
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.put(`/ai-documents/document/${documentId}/update-content`, {
        content: content,
        title: title
      });
      
      if (response.data.status === 'success') {
        setHasUnsavedChanges(false);
        setShowSuccess(true);
        
        // Atualizar documento local
        if (document) {
          setDocument({
            ...document,
            title: title,
            content: content,
            updated_at: new Date().toISOString()
          });
        }
      }
    } catch (err: any) {
      console.error('Erro ao salvar documento:', err);
      setError('Erro ao salvar documento');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      setError(null);
      
      // Salvar antes de baixar se houver alterações
      if (hasUnsavedChanges) {
        await handleSave();
      }
      
      const response = await api.get(`/ai-documents/document/${documentId}/download-docx`, {
        responseType: 'blob'
      });
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${title || 'documento'}.docx`);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      console.error('Erro ao baixar documento:', err);
      setError('Erro ao baixar documento');
    } finally {
      setDownloading(false);
    }
  };

  const getPreviewContent = () => {
    // Converter HTML para texto mais legível para preview
    const tempDiv = window.document.createElement('div');
    tempDiv.innerHTML = content;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  const toggleFullscreen = () => {
    setFullscreen(!fullscreen);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!document) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Documento não encontrado</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ height: fullscreen ? '100vh' : 'auto' }}>
      {/* Header/Toolbar */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Editor de Documento
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Visualizar">
              <IconButton onClick={() => setPreviewOpen(true)}>
                <PreviewIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={fullscreen ? "Sair da tela cheia" : "Tela cheia"}>
              <IconButton onClick={toggleFullscreen}>
                {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            
            <Button
              variant="outlined"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || !hasUnsavedChanges}
              size="small"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
            
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleDownload}
              disabled={downloading}
              size="small"
            >
              {downloading ? 'Baixando...' : 'Baixar DOCX'}
            </Button>
            
            {!fullscreen && (
              <IconButton onClick={() => navigate('/documents')}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth={fullscreen ? false : "xl"} sx={{ py: 2, height: fullscreen ? 'calc(100vh - 64px)' : 'auto' }}>
        <Grid container spacing={3} sx={{ height: '100%' }}>
          {/* Título */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Título do Documento"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
            />
          </Grid>

          {/* Editor */}
          <Grid item xs={12}>
            <Paper 
              ref={editorContainerRef}
              elevation={2} 
              sx={{ height: fullscreen ? 'calc(100vh - 200px)' : '600px' }}
            >
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={content}
                onChange={handleContentChange}
                modules={modules}
                formats={formats}
                style={{ height: fullscreen ? 'calc(100vh - 280px)' : '540px' }}
                placeholder="Comece a editar seu documento jurídico..."
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Preview Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Visualização do Documento
          <IconButton
            aria-label="close"
            onClick={() => setPreviewOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography
            variant="body1"
            component="div"
            sx={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'Times New Roman, serif',
              lineHeight: 1.6,
              maxHeight: '400px',
              overflow: 'auto'
            }}
          >
            {getPreviewContent()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        message="Documento salvo com sucesso!"
      />

      {/* Feedback Widget - Canto inferior esquerdo */}
      <FeedbackWidget
        feature={feedbackFeature as any}
        entityType="document"
        entityId={documentId}
        position="bottom-left"
        title="Avalie o documento"
        compact={true}
        onFeedbackSubmitted={(rating, comment) => {
          console.log('Feedback enviado:', { rating, comment });
        }}
      />

      {/* Botão de feedback para texto selecionado */}
      {isTextSelected && showFeedbackButton && (
        <Box
          sx={{
            position: 'fixed',
            left: buttonPosition.x - 60,
            top: buttonPosition.y,
            zIndex: 1100,
          }}
        >
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => setShowTextFeedback(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 3,
              fontSize: '0.75rem',
            }}
          >
            Feedback do texto
          </Button>
        </Box>
      )}

      {/* Modal de feedback para texto selecionado */}
      {showTextFeedback && selectedText && (
        <FeedbackWidget
          feature={feedbackFeature as any}
          entityType="document"
          entityId={documentId}
          position="inline"
          title="Avalie este texto"
          compact={false}
          open={true}
          selectedText={selectedText.text}
          textContext={getTextContext()}
          onFeedbackSubmitted={() => {
            clearSelection();
            setShowTextFeedback(false);
          }}
          onClose={() => {
            clearSelection();
            setShowTextFeedback(false);
          }}
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1200,
          }}
        />
      )}

      {/* Overlay para modal de feedback de texto */}
      {showTextFeedback && (
        <Box
          onClick={() => {
            clearSelection();
            setShowTextFeedback(false);
          }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1100,
          }}
        />
      )}
    </Box>
  );
};

export default DocumentEditorPage; 